﻿import React from 'react'
import { Heading, Text, Input, Select, Button, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { comercialGestaoService } from '../../services/comercialGestao'
import { userLogsService, type LogAction } from '../../services/userLogs'
import type {
  BrokerClassificationKpi,
  BrokerComercialGestaoItem,
  BrokerComercialRuleInput,
  BrokerContact,
  ClassificationTrend,
  ComercialUser,
  ConsultantUser,
} from '../../types/comercial'
import { ArrowDownTrayIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { downloadXlsxMulti } from '../../utils/downloadXlsx'
import { ClassificationWithTrend } from '../../components/ClassificationTrendIndicator'
import { ClassificationKpiCards } from '../../components/ClassificationKpiCards'
import { buildClassificationColorMap, buildClassificationRankMap, formatClassificationUpgradeHint, normalizeClassificationKey, resolveClassificationProgress, resolveClassificationTrend } from '../../utils/classificationTrend'
import { formatCurrencyBRL } from '../../utils/cotacaoFormatters'
import { BR_UFS } from '../../utils/brUfs'

type ActiveTab = 'listagem' | 'gestao_comercial' | 'gestao_consultor'
type GestaoParametroTab = 'distribuicao_automatica' | 'gestao_manual'
type GestaoAutoSubTab = 'distribuicao' | 'definido_automaticamente'
type GestaoManualSubTab = 'gestao_manual' | 'definido_manualmente'

const gestaoParamTabClass = (active: GestaoParametroTab, tab: GestaoParametroTab) =>
  `py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
    active === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

const gestaoSubTabClass = (active: string, tab: string) =>
  `py-2 px-3 border-b-2 text-sm whitespace-nowrap ${
    active === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400 font-medium'
      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
  }`

/** Valor do Select no modal Redistribuir: remove o vínculo em vez de trocar o comercial. */
const REDISTRIBUTE_CLEAR_COMMERCIAL = '__clear_comercial__'
const REDISTRIBUTE_CLEAR_CONSULTANT = '__clear_consultant__'

const COMERCIAL_GESTAO_LOG_ORIGEM = 'comercial/gestao'

function ruleSnapshotForLog(
  r: Pick<BrokerComercialRuleInput, 'name' | 'categories' | 'states' | 'is_fallback' | 'priority' | 'comercial_user_id' | 'is_active'> & {
    id?: string
  }
) {
  return {
    id: r.id,
    name: r.name,
    categories: r.categories,
    states: r.states,
    is_fallback: r.is_fallback,
    priority: r.priority,
    comercial_user_id: r.comercial_user_id,
    is_active: r.is_active,
  }
}

async function logComercialGestao(
  action: LogAction,
  description: string,
  options?: { entityId?: string | null; metadata?: Record<string, unknown> }
) {
  try {
    await userLogsService.logUserAction({
      action,
      entityType: 'other',
      entityId: options?.entityId ?? null,
      description,
      metadata: { origem: COMERCIAL_GESTAO_LOG_ORIGEM, ...(options?.metadata || {}) },
    })
  } catch (logError) {
    console.warn('Erro ao registrar log (comercial gestão):', logError)
  }
}

/** Nome completo do estado para exibição; persistência continua usando apenas a UF. */
const BR_STATE_NAMES: Record<string, string> = {
  AC: 'Acre',
  AL: 'Alagoas',
  AP: 'Amapá',
  AM: 'Amazonas',
  BA: 'Bahia',
  CE: 'Ceará',
  DF: 'Distrito Federal',
  ES: 'Espírito Santo',
  GO: 'Goiás',
  MA: 'Maranhão',
  MT: 'Mato Grosso',
  MS: 'Mato Grosso do Sul',
  MG: 'Minas Gerais',
  PA: 'Pará',
  PB: 'Paraíba',
  PR: 'Paraná',
  PE: 'Pernambuco',
  PI: 'Piauí',
  RJ: 'Rio de Janeiro',
  RN: 'Rio Grande do Norte',
  RS: 'Rio Grande do Sul',
  RO: 'Rondônia',
  RR: 'Roraima',
  SC: 'Santa Catarina',
  SP: 'São Paulo',
  SE: 'Sergipe',
  TO: 'Tocantins',
}

/** Insere quebra de linha a cada `chunkSize` caracteres (lista de UFs longa na tabela). */
function breakTextEvery(text: string, chunkSize: number): string {
  if (!text) return ''
  const parts: string[] = []
  for (let i = 0; i < text.length; i += chunkSize) {
    parts.push(text.slice(i, i + chunkSize))
  }
  return parts.join('\n')
}

const ContactEmailCell: React.FC<{ email: string }> = ({ email }) => {
  const parts = email
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)

  if (parts.length === 0) return <>—</>

  const linkClass = 'text-primary-600 dark:text-primary-400 hover:underline break-all'

  if (parts.length === 1) {
    return (
      <a href={`mailto:${parts[0]}`} className={linkClass} onClick={(e) => e.stopPropagation()}>
        {parts[0]}
      </a>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      {parts.map((part) => (
        <a
          key={part}
          href={`mailto:${part}`}
          className={linkClass}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      ))}
    </div>
  )
}

const StartProductionBadge: React.FC<{ hasProduction: boolean }> = ({ hasProduction }) => (
  <span
    className={`inline-flex items-center rounded px-1 py-px text-[10px] leading-tight font-normal ${
      hasProduction
        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
    }`}
    title={
      hasProduction
        ? 'START com produção (prêmio pago mensal maior que zero)'
        : 'START sem produção'
    }
  >
    {hasProduction ? 'Com produção' : 'Sem produção'}
  </span>
)

const ClassificationCell: React.FC<{
  classification: string | null
  classificationPrevious: string | null
  rankMap: Map<string, number>
  colorMap: Map<string, string | null>
  hasProduction?: boolean
  monthlyPaidPremium?: number
  classificationBands?: Array<{ name: string; min_value: number }>
}> = ({
  classification,
  classificationPrevious,
  rankMap,
  colorMap,
  hasProduction,
  monthlyPaidPremium,
  classificationBands,
}) => {
  const isStart = normalizeClassificationKey(classification) === 'START'
  const progress =
    classificationBands && classificationBands.length > 0
      ? resolveClassificationProgress(monthlyPaidPremium ?? 0, classificationBands)
      : null
  const upgradeHint = progress
    ? formatClassificationUpgradeHint(progress, formatCurrencyBRL, classification)
    : null

  return (
    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
      <div className="flex flex-col items-start gap-0.5">
        <ClassificationWithTrend
          classification={classification}
          classificationPrevious={classificationPrevious}
          rankMap={rankMap}
          colorMap={colorMap}
        />
        {isStart && <StartProductionBadge hasProduction={!!hasProduction} />}
        {upgradeHint && (
          <span
            className={`text-[10px] leading-tight ${
              progress?.is_near_upgrade
                ? 'text-amber-700 dark:text-amber-300 font-medium'
                : 'text-gray-500 dark:text-gray-400'
            }`}
            title={upgradeHint}
          >
            {upgradeHint}
          </span>
        )}
      </div>
    </td>
  )
}

const CheckboxGrid: React.FC<{
  options: readonly string[]
  selected: string[]
  disabled?: boolean
  onToggle: (value: string, checked: boolean) => void
  /** Rótulo na tela; `onToggle` continua recebendo o valor de `options` (ex.: UF). */
  getDisplayLabel?: (value: string) => string
}> = ({ options, selected, disabled, onToggle, getDisplayLabel }) => {
  const selectedSet = new Set(selected.map((item) => item.toUpperCase()))

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 rounded-lg border border-gray-300 dark:border-gray-600 p-3 bg-white dark:bg-gray-800">
      {options.map((option) => {
        const checked = selectedSet.has(option.toUpperCase())
        const label = getDisplayLabel ? getDisplayLabel(option) : option
        return (
          <label key={option} className="inline-flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100">
            <input
              type="checkbox"
              checked={checked}
              disabled={disabled}
              onChange={(e) => onToggle(option, e.target.checked)}
            />
            <span>{label}</span>
          </label>
        )
      })}
    </div>
  )
}

type StartProductionFilter = '' | 'with' | 'without'
type ClassificationTrendFilter = '' | ClassificationTrend | 'none'

const GestaoComercial: React.FC = () => {
  const { profile } = useAuthContext()
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('listagem')
  const [gestaoParamTab, setGestaoParamTab] = React.useState<GestaoParametroTab>('distribuicao_automatica')
  const [gestaoAutoSubTab, setGestaoAutoSubTab] = React.useState<GestaoAutoSubTab>('distribuicao')
  const [gestaoManualSubTab, setGestaoManualSubTab] = React.useState<GestaoManualSubTab>('gestao_manual')
  const [gestaoConsultorSubTab, setGestaoConsultorSubTab] = React.useState<GestaoManualSubTab>('gestao_manual')
  const [loading, setLoading] = React.useState(true)
  const [submittingAuto, setSubmittingAuto] = React.useState(false)
  const [submittingManual, setSubmittingManual] = React.useState(false)
  const [submittingConsultantManual, setSubmittingConsultantManual] = React.useState(false)
  const [submittingNewRule, setSubmittingNewRule] = React.useState(false)
  const [deletingRule, setDeletingRule] = React.useState(false)
  const [items, setItems] = React.useState<BrokerComercialGestaoItem[]>([])
  const [manualBrokerIds, setManualBrokerIds] = React.useState<Set<string>>(new Set())
  const [comercialUsers, setComercialUsers] = React.useState<ComercialUser[]>([])
  const [consultantUsers, setConsultantUsers] = React.useState<ConsultantUser[]>([])
  const [availableCategories, setAvailableCategories] = React.useState<string[]>([])
  const [rules, setRules] = React.useState<BrokerComercialRuleInput[]>([])
  const [moduleInfo, setModuleInfo] = React.useState<{ display_name: string; description?: string | null } | null>(null)
  const [selectedBrokers, setSelectedBrokers] = React.useState<Set<string>>(new Set())
  const [selectedConsultantBrokers, setSelectedConsultantBrokers] = React.useState<Set<string>>(new Set())
  const [manualComercialId, setManualComercialId] = React.useState('')
  const [manualConsultantId, setManualConsultantId] = React.useState('')
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [loadingBrokers, setLoadingBrokers] = React.useState(false)
  const [loadingGestaoTab, setLoadingGestaoTab] = React.useState(false)
  const [hasSearchedListagem, setHasSearchedListagem] = React.useState(false)
  const [isNewRuleModalOpen, setIsNewRuleModalOpen] = React.useState(false)
  const [editingRuleId, setEditingRuleId] = React.useState<string | null>(null)
  const [ruleIndexToRemove, setRuleIndexToRemove] = React.useState<number | null>(null)
  const [newRuleDraft, setNewRuleDraft] = React.useState<BrokerComercialRuleInput>({
    name: '',
    categories: [],
    states: [],
    is_fallback: false,
    priority: 1,
    comercial_user_id: '',
    is_active: true,
  })

  const [filterName, setFilterName] = React.useState('')
  const [filterCode, setFilterCode] = React.useState('')
  const [filterClassification, setFilterClassification] = React.useState('')
  const [filterClassificationTrend, setFilterClassificationTrend] = React.useState<ClassificationTrendFilter>('')
  const [filterStartProduction, setFilterStartProduction] = React.useState<StartProductionFilter>('')
  const [filterUf, setFilterUf] = React.useState('')
  const [filterComercial, setFilterComercial] = React.useState('')
  const [filterConsultant, setFilterConsultant] = React.useState('')
  const [appliedFilters, setAppliedFilters] = React.useState({
    name: '',
    code: '',
    classification: '',
    classificationTrend: '' as ClassificationTrendFilter,
    startProduction: '' as StartProductionFilter,
    uf: '',
    comercial: '',
    consultant: '',
  })
  const [currentPageListagem, setCurrentPageListagem] = React.useState(1)
  const [currentPageRules, setCurrentPageRules] = React.useState(1)
  const [currentPagePendentes, setCurrentPagePendentes] = React.useState(1)
  const [currentPageManualAssigned, setCurrentPageManualAssigned] = React.useState(1)
  const [currentPageConsultantPendentes, setCurrentPageConsultantPendentes] = React.useState(1)
  const [currentPageConsultantAssigned, setCurrentPageConsultantAssigned] = React.useState(1)
  const [currentPageAutoAssigned, setCurrentPageAutoAssigned] = React.useState(1)
  const [redistributeModalBroker, setRedistributeModalBroker] = React.useState<BrokerComercialGestaoItem | null>(null)
  const [redistributeComercialId, setRedistributeComercialId] = React.useState('')
  const [submittingRedistribute, setSubmittingRedistribute] = React.useState(false)
  const [redistributeConsultantModalBroker, setRedistributeConsultantModalBroker] = React.useState<BrokerComercialGestaoItem | null>(null)
  const [redistributeConsultantId, setRedistributeConsultantId] = React.useState('')
  const [submittingConsultantRedistribute, setSubmittingConsultantRedistribute] = React.useState(false)
  const [contactsModalBroker, setContactsModalBroker] = React.useState<BrokerComercialGestaoItem | null>(null)
  const [contactsModalItems, setContactsModalItems] = React.useState<BrokerContact[]>([])
  const [contactsModalLoading, setContactsModalLoading] = React.useState(false)
  const [contactsModalError, setContactsModalError] = React.useState<string | null>(null)
  const [filterManualPendingName, setFilterManualPendingName] = React.useState('')
  const [filterManualAssignedName, setFilterManualAssignedName] = React.useState('')
  const [filterConsultantPendingName, setFilterConsultantPendingName] = React.useState('')
  const [filterConsultantAssignedName, setFilterConsultantAssignedName] = React.useState('')
  const [filterAutoAssignedName, setFilterAutoAssignedName] = React.useState('')
  const [classificationKpis, setClassificationKpis] = React.useState<BrokerClassificationKpi[]>([])
  const [loadingClassificationKpis, setLoadingClassificationKpis] = React.useState(false)
  const [classificationKpisError, setClassificationKpisError] = React.useState<string | null>(null)
  const [exportingExcel, setExportingExcel] = React.useState(false)
  const itemsPerPage = 20

  /** Cadastro (insert): obrigatório para ver as abas de atribuição. */
  const { hasPermission: canInsert, loading: loadingCadastroPermission } = usePermission('comercial_gestao', 'insert')
  const { hasPermission: canUpdate } = usePermission('comercial_gestao', 'update')
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission('comercial_gestao', 'view_values')
  const { hasPermission: canDownloadInfo } = usePermission('comercial_gestao', 'download_info')
  const canManage = canInsert || canUpdate

  const loggedUserName = profile?.name?.trim() ?? ''
  const isComercialFilterRestricted = loadingViewValues || !canViewValues
  const displayedComercialFilter = isComercialFilterRestricted ? loggedUserName : filterComercial
  const effectiveComercialFilter = isComercialFilterRestricted ? (loggedUserName || '\0') : filterComercial

  const getErrorMessage = (error: unknown) => {
    if (error instanceof Error) return error.message
    const maybe = error as { message?: string; code?: string }
    if (maybe?.message) return maybe.message
    if (maybe?.code) return `Erro (${maybe.code})`
    return 'Erro inesperado ao processar solicitação.'
  }

  const loadStaticData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const [users, consultants, loadedRules, categories] = await Promise.all([
        comercialGestaoService.listComercialUsers(),
        comercialGestaoService.listConsultantUsers(),
        comercialGestaoService.listRules(),
        comercialGestaoService.listAvailableCategories(),
      ])
      setComercialUsers(users)
      setConsultantUsers(consultants)
      setAvailableCategories(categories)
      setRules(
        loadedRules.map((r) => ({
          id: r.id,
          name: r.name,
          categories: r.categories || [],
          states: r.states || [],
          is_fallback: r.is_fallback,
          priority: r.priority,
          comercial_user_id: r.comercial_user_id,
          is_active: r.is_active,
        }))
      )
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  const loadClassificationKpis = React.useCallback(async () => {
    setLoadingClassificationKpis(true)
    setClassificationKpisError(null)
    try {
      const kpis = await comercialGestaoService.getBrokerCountsByClassificationKpis()
      setClassificationKpis(kpis)
    } catch (error) {
      setClassificationKpisError(getErrorMessage(error))
    } finally {
      setLoadingClassificationKpis(false)
    }
  }, [])

  const loadBrokers = React.useCallback(async () => {
    setLoadingBrokers(true)
    setPageError(null)
    try {
      const [brokers, manualIds] = await Promise.all([
        comercialGestaoService.listBrokersWithAssignments(),
        comercialGestaoService.fetchManualBrokerIds(),
      ])
      setItems(brokers)
      setManualBrokerIds(manualIds)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoadingBrokers(false)
    }
  }, [loadClassificationKpis])

  const loadGestaoTabData = React.useCallback(async () => {
    setLoadingGestaoTab(true)
    setPageError(null)
    try {
      const [users, consultants, loadedRules, categories, brokers, manualIds] = await Promise.all([
        comercialGestaoService.listComercialUsers(),
        comercialGestaoService.listConsultantUsers(),
        comercialGestaoService.listRules(),
        comercialGestaoService.listAvailableCategories(),
        comercialGestaoService.listBrokersWithAssignments(),
        comercialGestaoService.fetchManualBrokerIds(),
      ])
      setComercialUsers(users)
      setConsultantUsers(consultants)
      setAvailableCategories(categories)
      setRules(
        loadedRules.map((r) => ({
          id: r.id,
          name: r.name,
          categories: r.categories || [],
          states: r.states || [],
          is_fallback: r.is_fallback,
          priority: r.priority,
          comercial_user_id: r.comercial_user_id,
          is_active: r.is_active,
        }))
      )
      setItems(brokers)
      setManualBrokerIds(manualIds)
      await loadClassificationKpis()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoadingGestaoTab(false)
    }
  }, [loadClassificationKpis])

  /** Ao ir para Atribuição de Comercial/Consultor: zera filtros da listagem e recarrega todos os corretores. */
  const goToGestaoTab = React.useCallback((target: ActiveTab) => {
    setFilterName('')
    setFilterCode('')
    setFilterClassification('')
    setFilterClassificationTrend('')
    setFilterStartProduction('')
    setFilterUf('')
    setFilterComercial(isComercialFilterRestricted ? loggedUserName : '')
    setFilterConsultant('')
    setAppliedFilters({
      name: '',
      code: '',
      classification: '',
      classificationTrend: '',
      startProduction: '',
      uf: '',
      comercial: isComercialFilterRestricted ? loggedUserName : '',
      consultant: '',
    })
    setCurrentPageListagem(1)
    setHasSearchedListagem(true)
    setGestaoParamTab('distribuicao_automatica')
    setGestaoAutoSubTab('distribuicao')
    setGestaoManualSubTab('gestao_manual')
    setGestaoConsultorSubTab('gestao_manual')
    setSelectedBrokers(new Set())
    setSelectedConsultantBrokers(new Set())
    setManualComercialId('')
    setManualConsultantId('')
    setActiveTab(target)
    void loadGestaoTabData()
  }, [isComercialFilterRestricted, loadGestaoTabData, loggedUserName])

  React.useEffect(() => {
    void loadStaticData()
  }, [loadStaticData])

  React.useEffect(() => {
    void loadClassificationKpis()
  }, [loadClassificationKpis])

  React.useEffect(() => {
    if (!loadingCadastroPermission && !canInsert && (activeTab === 'gestao_comercial' || activeTab === 'gestao_consultor')) {
      setActiveTab('listagem')
    }
  }, [loadingCadastroPermission, canInsert, activeTab])

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName('comercial_gestao')
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || 'Gestão Comercial',
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  const classificationFilterOptions = React.useMemo(() => {
    if (classificationKpis.length > 0) {
      return classificationKpis.map((kpi) => kpi.name).filter(Boolean)
    }
    return [...availableCategories].sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [classificationKpis, availableCategories])

  const classificationRankMap = React.useMemo(
    () =>
      buildClassificationRankMap(
        classificationKpis.map((kpi) => ({ name: kpi.name, min_value: kpi.min_value }))
      ),
    [classificationKpis]
  )

  const classificationBands = React.useMemo(
    () => classificationKpis.map((kpi) => ({ name: kpi.name, min_value: kpi.min_value })),
    [classificationKpis]
  )

  const classificationColorMap = React.useMemo(
    () => buildClassificationColorMap(classificationKpis.map((kpi) => ({ name: kpi.name, color: kpi.color }))),
    [classificationKpis]
  )

  const ufs = React.useMemo(
    () => Array.from(new Set(items.map((i) => (i.state || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [items]
  )

  const isStartClassificationSelected =
    normalizeClassificationKey(filterClassification) === 'START'

  const filteredItems = React.useMemo(() => {
    return items.filter((item) => {
      if (appliedFilters.name.trim() && !item.name.toLowerCase().includes(appliedFilters.name.toLowerCase().trim())) return false
      if (appliedFilters.code.trim()) {
        const want = appliedFilters.code.trim()
        if (item.id_scgp == null || String(item.id_scgp) !== want) return false
      }
      if (
        appliedFilters.classification &&
        normalizeClassificationKey(item.classification) !== normalizeClassificationKey(appliedFilters.classification)
      ) {
        return false
      }
      if (
        appliedFilters.startProduction &&
        normalizeClassificationKey(appliedFilters.classification) === 'START' &&
        normalizeClassificationKey(item.classification) === 'START'
      ) {
        const hasProduction = !!item.has_production
        if (appliedFilters.startProduction === 'with' && !hasProduction) return false
        if (appliedFilters.startProduction === 'without' && hasProduction) return false
      }
      if (appliedFilters.classificationTrend) {
        const trend = resolveClassificationTrend(
          item.classification,
          item.classification_previous,
          classificationRankMap
        )
        if (appliedFilters.classificationTrend === 'none') {
          if (trend) return false
        } else if (trend !== appliedFilters.classificationTrend) {
          return false
        }
      }
      if (appliedFilters.uf && (item.state || '').trim() !== appliedFilters.uf) return false
      if (appliedFilters.comercial && (item.comercial_name || '').trim() !== appliedFilters.comercial) return false
      if (appliedFilters.consultant && (item.consultant_name || '').trim() !== appliedFilters.consultant) return false
      return true
    })
  }, [items, appliedFilters, classificationRankMap])

  const hasListagemFiltersActive = React.useMemo(
    () =>
      !!(
        filterName.trim() ||
        filterCode.trim() ||
        filterClassification ||
        filterClassificationTrend ||
        (isStartClassificationSelected && filterStartProduction) ||
        filterUf ||
        (!isComercialFilterRestricted && filterComercial) ||
        filterConsultant
      ),
    [filterName, filterCode, filterClassification, filterClassificationTrend, filterStartProduction, isStartClassificationSelected, filterUf, filterComercial, filterConsultant, isComercialFilterRestricted]
  )

  const totalPagesListagem = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage))
  const pagedListagem = React.useMemo(() => {
    const start = (currentPageListagem - 1) * itemsPerPage
    return filteredItems.slice(start, start + itemsPerPage)
  }, [filteredItems, currentPageListagem])

  const applyListagemFilters = () => {
    setAppliedFilters({
      name: filterName,
      code: filterCode,
      classification: filterClassification,
      classificationTrend: filterClassificationTrend,
      startProduction: isStartClassificationSelected ? filterStartProduction : '',
      uf: filterUf,
      comercial: effectiveComercialFilter,
      consultant: filterConsultant,
    })
    setCurrentPageListagem(1)
    setHasSearchedListagem(true)
    void loadBrokers()
  }

  const exportListagemExcel = async () => {
    if (!canDownloadInfo || !hasSearchedListagem || filteredItems.length === 0 || exportingExcel) return

    const brokerRows = filteredItems.map((item) => {
      const category = (item.classification || '').trim() || ''
      const categoryLabel =
        normalizeClassificationKey(item.classification) === 'START' && category
          ? `${category} (${item.has_production ? 'Com produção' : 'Sem produção'})`
          : category
      const progress = resolveClassificationProgress(item.monthly_paid_premium ?? 0, classificationBands)

      return {
        Corretor: item.name,
        'Código SCGP': item.id_scgp ?? '',
        Categoria: categoryLabel,
        'Próxima categoria': progress.next_classification_name ?? 'Categoria máxima',
        'Falta para upgrade':
          progress.gap_to_next_classification != null
            ? formatCurrencyBRL(progress.gap_to_next_classification)
            : '',
        'UF/Cidade': [item.state, item.city].filter(Boolean).join(' / ') || '',
        Comercial: (item.comercial_name || '').trim() || '',
        Consultor: (item.consultant_name || '').trim() || '',
      }
    })

    setExportingExcel(true)
    setPageError(null)
    try {
      const brokerById = new Map(filteredItems.map((item) => [item.id, item]))
      const contacts = await comercialGestaoService.listBrokerContactsByBrokerIds(filteredItems.map((i) => i.id))
      const contactRows = contacts.map((contact) => {
        const broker = brokerById.get(contact.broker_id)
        return {
          Corretor: broker?.name ?? '',
          'Código SCGP': broker?.id_scgp ?? '',
          Nome: contact.name || '',
          Sócio: contact.is_partner ? 'Sim' : 'Não',
          Departamento: contact.department || '',
          Cargo: contact.role || '',
          Telefone: contact.phone || '',
          'E-mail': contact.email || '',
        }
      })

      const dateStamp = new Date().toISOString().slice(0, 10)
      downloadXlsxMulti(
        [
          { sheetName: 'Relação Completa', rows: brokerRows },
          { sheetName: 'Contatos', rows: contactRows },
        ],
        `relacao-completa-corretores-${dateStamp}.xlsx`
      )
      void logComercialGestao('export', `Comercial gestão: exportou relação completa (${filteredItems.length} corretor(es)).`, {
        metadata: { row_count: filteredItems.length, contact_row_count: contactRows.length, filters: appliedFilters },
      })
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setExportingExcel(false)
    }
  }

  const preview = React.useMemo(
    () => comercialGestaoService.buildAutoAssignmentPreview(items, rules, comercialUsers, manualBrokerIds),
    [items, rules, comercialUsers, manualBrokerIds]
  )

  const manualPendingItems = React.useMemo(
    () => items.filter((i) => !i.assignment?.comercial_user_id),
    [items]
  )

  const manualAssignedItems = React.useMemo(
    () =>
      items.filter(
        (i) => i.assignment?.assignment_type === 'manual' && !!i.assignment?.comercial_user_id
      ),
    [items]
  )

  const consultantPendingItems = React.useMemo(
    () => items.filter((i) => !i.consultant_assignment?.consultant_user_id),
    [items]
  )

  const consultantAssignedItems = React.useMemo(
    () =>
      items.filter(
        (i) =>
          i.consultant_assignment?.assignment_type === 'manual' &&
          !!i.consultant_assignment?.consultant_user_id
      ),
    [items]
  )

  const autoAssignedItems = React.useMemo(
    () =>
      items.filter(
        (i) => i.assignment?.assignment_type === 'auto' && !!i.assignment?.comercial_user_id
      ),
    [items]
  )

  const brokerNameMatches = (name: string, query: string) => {
    const q = query.trim().toLowerCase()
    if (!q) return true
    return name.toLowerCase().includes(q)
  }

  const manualPendingFiltered = React.useMemo(
    () => manualPendingItems.filter((i) => brokerNameMatches(i.name, filterManualPendingName)),
    [manualPendingItems, filterManualPendingName]
  )

  const manualAssignedFiltered = React.useMemo(
    () => manualAssignedItems.filter((i) => brokerNameMatches(i.name, filterManualAssignedName)),
    [manualAssignedItems, filterManualAssignedName]
  )

  const consultantPendingFiltered = React.useMemo(
    () => consultantPendingItems.filter((i) => brokerNameMatches(i.name, filterConsultantPendingName)),
    [consultantPendingItems, filterConsultantPendingName]
  )

  const consultantAssignedFiltered = React.useMemo(
    () => consultantAssignedItems.filter((i) => brokerNameMatches(i.name, filterConsultantAssignedName)),
    [consultantAssignedItems, filterConsultantAssignedName]
  )

  const autoAssignedFiltered = React.useMemo(
    () => autoAssignedItems.filter((i) => brokerNameMatches(i.name, filterAutoAssignedName)),
    [autoAssignedItems, filterAutoAssignedName]
  )

  const totalPagesRules = Math.max(1, Math.ceil(rules.length / itemsPerPage))
  const pagedRules = React.useMemo(() => {
    const start = (currentPageRules - 1) * itemsPerPage
    return rules.slice(start, start + itemsPerPage)
  }, [rules, currentPageRules])

  const totalPagesPendentes = Math.max(1, Math.ceil(manualPendingFiltered.length / itemsPerPage))
  const pagedPendentes = React.useMemo(() => {
    const start = (currentPagePendentes - 1) * itemsPerPage
    return manualPendingFiltered.slice(start, start + itemsPerPage)
  }, [manualPendingFiltered, currentPagePendentes])

  const totalPagesManualAssigned = Math.max(1, Math.ceil(manualAssignedFiltered.length / itemsPerPage))
  const pagedManualAssigned = React.useMemo(() => {
    const start = (currentPageManualAssigned - 1) * itemsPerPage
    return manualAssignedFiltered.slice(start, start + itemsPerPage)
  }, [manualAssignedFiltered, currentPageManualAssigned])

  const totalPagesConsultantPendentes = Math.max(1, Math.ceil(consultantPendingFiltered.length / itemsPerPage))
  const pagedConsultantPendentes = React.useMemo(() => {
    const start = (currentPageConsultantPendentes - 1) * itemsPerPage
    return consultantPendingFiltered.slice(start, start + itemsPerPage)
  }, [consultantPendingFiltered, currentPageConsultantPendentes])

  const totalPagesConsultantAssigned = Math.max(1, Math.ceil(consultantAssignedFiltered.length / itemsPerPage))
  const pagedConsultantAssigned = React.useMemo(() => {
    const start = (currentPageConsultantAssigned - 1) * itemsPerPage
    return consultantAssignedFiltered.slice(start, start + itemsPerPage)
  }, [consultantAssignedFiltered, currentPageConsultantAssigned])

  const totalPagesAutoAssigned = Math.max(1, Math.ceil(autoAssignedFiltered.length / itemsPerPage))
  const pagedAutoAssigned = React.useMemo(() => {
    const start = (currentPageAutoAssigned - 1) * itemsPerPage
    return autoAssignedFiltered.slice(start, start + itemsPerPage)
  }, [autoAssignedFiltered, currentPageAutoAssigned])

  React.useEffect(() => {
    setCurrentPagePendentes(1)
  }, [filterManualPendingName])

  React.useEffect(() => {
    setCurrentPageManualAssigned(1)
  }, [filterManualAssignedName])

  React.useEffect(() => {
    setCurrentPageConsultantPendentes(1)
  }, [filterConsultantPendingName])

  React.useEffect(() => {
    setCurrentPageConsultantAssigned(1)
  }, [filterConsultantAssignedName])

  React.useEffect(() => {
    setCurrentPageAutoAssigned(1)
  }, [filterAutoAssignedName])

  React.useEffect(() => {
    if (currentPagePendentes > totalPagesPendentes) setCurrentPagePendentes(totalPagesPendentes)
  }, [currentPagePendentes, totalPagesPendentes])

  React.useEffect(() => {
    if (currentPageManualAssigned > totalPagesManualAssigned) setCurrentPageManualAssigned(totalPagesManualAssigned)
  }, [currentPageManualAssigned, totalPagesManualAssigned])

  React.useEffect(() => {
    if (currentPageConsultantPendentes > totalPagesConsultantPendentes) {
      setCurrentPageConsultantPendentes(totalPagesConsultantPendentes)
    }
  }, [currentPageConsultantPendentes, totalPagesConsultantPendentes])

  React.useEffect(() => {
    if (currentPageConsultantAssigned > totalPagesConsultantAssigned) {
      setCurrentPageConsultantAssigned(totalPagesConsultantAssigned)
    }
  }, [currentPageConsultantAssigned, totalPagesConsultantAssigned])

  React.useEffect(() => {
    if (currentPageAutoAssigned > totalPagesAutoAssigned) setCurrentPageAutoAssigned(totalPagesAutoAssigned)
  }, [currentPageAutoAssigned, totalPagesAutoAssigned])

  const toggleBroker = (brokerId: string) => {
    setSelectedBrokers((prev) => {
      const next = new Set(prev)
      if (next.has(brokerId)) next.delete(brokerId)
      else next.add(brokerId)
      return next
    })
  }

  const toggleConsultantBroker = (brokerId: string) => {
    setSelectedConsultantBrokers((prev) => {
      const next = new Set(prev)
      if (next.has(brokerId)) next.delete(brokerId)
      else next.add(brokerId)
      return next
    })
  }

  const toggleSelectAllManualPending = () => {
    const visibleIds = pagedPendentes.map((i) => i.id)
    const allSelected = visibleIds.every((id) => selectedBrokers.has(id))
    setSelectedBrokers((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id))
      } else {
        visibleIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const toggleSelectAllConsultantPending = () => {
    const visibleIds = pagedConsultantPendentes.map((i) => i.id)
    const allSelected = visibleIds.every((id) => selectedConsultantBrokers.has(id))
    setSelectedConsultantBrokers((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id))
      } else {
        visibleIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const handleApplyAuto = async () => {
    if (!canManage) return
    setSubmittingAuto(true)
    setPageError(null)
    try {
      const [freshItems, freshManualIds] = await Promise.all([
        comercialGestaoService.listBrokersWithAssignments(),
        comercialGestaoService.fetchManualBrokerIds(),
      ])
      setItems(freshItems)
      setManualBrokerIds(freshManualIds)
      const result = await comercialGestaoService.applyAutoAssignments(freshItems, rules)
      await logComercialGestao(
        'update',
        `Comercial gestão: aplicou distribuição automática (${result.updated} corretores atualizados, ${result.cleared} limpos, ${result.skipped} ignorados).`,
        { metadata: { updated: result.updated, cleared: result.cleared, skipped: result.skipped } }
      )
      await loadBrokers()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingAuto(false)
    }
  }

  const handleAssignManual = async () => {
    if (!canManage || !manualComercialId || selectedBrokers.size === 0) return
    setSubmittingManual(true)
    setPageError(null)
    const brokerIds = Array.from(selectedBrokers)
    const brokersSelected = items.filter((i) => brokerIds.includes(i.id))
    const idScgpList = brokersSelected
      .map((i) => i.id_scgp)
      .filter((v): v is number => v != null && Number.isFinite(Number(v)))
    const comercialLabel = comercialUsers.find((u) => u.id === manualComercialId)?.name ?? manualComercialId
    const scgpText = idScgpList.length > 0 ? idScgpList.join(', ') : 'sem código SCGP'
    try {
      await comercialGestaoService.assignManual({
        brokerIds,
        comercialUserId: manualComercialId,
      })
      await logComercialGestao(
        'update',
        `Comercial gestão: atribuição manual do comercial "${comercialLabel}" — SCGP: ${scgpText}.`,
        {
          metadata: {
            comercial_user_id: manualComercialId,
            comercial_name: comercialLabel,
            id_scgp_list: idScgpList,
            broker_ids: brokerIds,
            broker_names: brokersSelected.map((b) => b.name),
          },
        }
      )
      setSelectedBrokers(new Set())
      await loadBrokers()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingManual(false)
    }
  }

  const handleAssignConsultantManual = async () => {
    if (!canManage || !manualConsultantId || selectedConsultantBrokers.size === 0) return
    setSubmittingConsultantManual(true)
    setPageError(null)
    const brokerIds = Array.from(selectedConsultantBrokers)
    const brokersSelected = items.filter((i) => brokerIds.includes(i.id))
    const idScgpList = brokersSelected
      .map((i) => i.id_scgp)
      .filter((v): v is number => v != null && Number.isFinite(Number(v)))
    const consultantLabel =
      consultantUsers.find((u) => u.id === manualConsultantId)?.name ?? manualConsultantId
    const scgpText = idScgpList.length > 0 ? idScgpList.join(', ') : 'sem código SCGP'
    try {
      await comercialGestaoService.assignConsultantManual({
        brokerIds,
        consultantUserId: manualConsultantId,
      })
      await logComercialGestao(
        'update',
        `Comercial gestão: atribuição manual do consultor "${consultantLabel}" — SCGP: ${scgpText}.`,
        {
          metadata: {
            consultant_user_id: manualConsultantId,
            consultant_name: consultantLabel,
            id_scgp_list: idScgpList,
            broker_ids: brokerIds,
            broker_names: brokersSelected.map((b) => b.name),
          },
        }
      )
      setSelectedConsultantBrokers(new Set())
      await loadBrokers()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingConsultantManual(false)
    }
  }

  const openRedistributeModal = (item: BrokerComercialGestaoItem) => {
    setRedistributeModalBroker(item)
    setRedistributeComercialId(item.assignment?.comercial_user_id || '')
  }

  const openConsultantRedistributeModal = (item: BrokerComercialGestaoItem) => {
    setRedistributeConsultantModalBroker(item)
    setRedistributeConsultantId(item.consultant_assignment?.consultant_user_id || '')
  }

  const closeRedistributeModal = () => {
    setRedistributeModalBroker(null)
    setRedistributeComercialId('')
  }

  const closeConsultantRedistributeModal = () => {
    setRedistributeConsultantModalBroker(null)
    setRedistributeConsultantId('')
  }

  const closeContactsModal = () => {
    setContactsModalBroker(null)
    setContactsModalItems([])
    setContactsModalLoading(false)
    setContactsModalError(null)
  }

  const loadContactsForBroker = React.useCallback(async (brokerId: string) => {
    setContactsModalLoading(true)
    setContactsModalError(null)
    try {
      const contacts = await comercialGestaoService.listBrokerContacts(brokerId)
      setContactsModalItems(contacts)
    } catch (error) {
      setContactsModalItems([])
      setContactsModalError(getErrorMessage(error))
    } finally {
      setContactsModalLoading(false)
    }
  }, [])

  const openContactsModal = (item: BrokerComercialGestaoItem) => {
    setContactsModalBroker(item)
    setContactsModalItems([])
    setContactsModalError(null)
    void loadContactsForBroker(item.id)
  }

  const handleConfirmRedistribute = async () => {
    if (!canManage || !redistributeModalBroker || !redistributeComercialId) return
    setSubmittingRedistribute(true)
    setPageError(null)
    const brokerSnap = redistributeModalBroker
    const newComercialId = redistributeComercialId
    const prevComercialId = brokerSnap.assignment?.comercial_user_id ?? null
    const prevComercialName = brokerSnap.comercial_name
    const scgpLabel = brokerSnap.id_scgp != null ? String(brokerSnap.id_scgp) : '—'
    try {
      if (newComercialId === REDISTRIBUTE_CLEAR_COMMERCIAL) {
        await comercialGestaoService.removeBrokerComercialAssignment(brokerSnap.id)
        await logComercialGestao(
          'update',
          `Comercial gestão: removeu vínculo manual do corretor "${brokerSnap.name}" (SCGP: ${scgpLabel}).`,
          {
            entityId: brokerSnap.id,
            metadata: {
              broker_id: brokerSnap.id,
              id_scgp: brokerSnap.id_scgp,
              action: 'remove_manual_assignment',
              previous_comercial_user_id: prevComercialId,
              previous_comercial_name: prevComercialName,
            },
          }
        )
      } else {
        await comercialGestaoService.assignManual({
          brokerIds: [brokerSnap.id],
          comercialUserId: newComercialId,
        })
        const newLabel = comercialUsers.find((u) => u.id === newComercialId)?.name ?? newComercialId
        await logComercialGestao(
          'update',
          `Comercial gestão: alterou comercial manual do corretor "${brokerSnap.name}" (SCGP: ${scgpLabel}) de "${prevComercialName ?? '—'}" para "${newLabel}".`,
          {
            entityId: brokerSnap.id,
            metadata: {
              broker_id: brokerSnap.id,
              id_scgp: brokerSnap.id_scgp,
              previous_comercial_user_id: prevComercialId,
              previous_comercial_name: prevComercialName,
              new_comercial_user_id: newComercialId,
              new_comercial_name: newLabel,
            },
          }
        )
      }
      closeRedistributeModal()
      await loadBrokers()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingRedistribute(false)
    }
  }

  const handleConfirmConsultantRedistribute = async () => {
    if (!canManage || !redistributeConsultantModalBroker || !redistributeConsultantId) return
    setSubmittingConsultantRedistribute(true)
    setPageError(null)
    const brokerSnap = redistributeConsultantModalBroker
    const newConsultantId = redistributeConsultantId
    const prevConsultantId = brokerSnap.consultant_assignment?.consultant_user_id ?? null
    const prevConsultantName = brokerSnap.consultant_name
    const scgpLabel = brokerSnap.id_scgp != null ? String(brokerSnap.id_scgp) : '—'

    try {
      if (newConsultantId === REDISTRIBUTE_CLEAR_CONSULTANT) {
        await comercialGestaoService.removeBrokerConsultantAssignment(brokerSnap.id)
        await logComercialGestao(
          'update',
          `Comercial gestão: removeu vínculo manual de consultor do corretor "${brokerSnap.name}" (SCGP: ${scgpLabel}).`,
          {
            entityId: brokerSnap.id,
            metadata: {
              broker_id: brokerSnap.id,
              id_scgp: brokerSnap.id_scgp,
              action: 'remove_manual_consultant_assignment',
              previous_consultant_user_id: prevConsultantId,
              previous_consultant_name: prevConsultantName,
            },
          }
        )
      } else {
        await comercialGestaoService.assignConsultantManual({
          brokerIds: [brokerSnap.id],
          consultantUserId: newConsultantId,
        })
        const newLabel =
          consultantUsers.find((u) => u.id === newConsultantId)?.name ?? newConsultantId
        await logComercialGestao(
          'update',
          `Comercial gestão: alterou consultor manual do corretor "${brokerSnap.name}" (SCGP: ${scgpLabel}) de "${prevConsultantName ?? '—'}" para "${newLabel}".`,
          {
            entityId: brokerSnap.id,
            metadata: {
              broker_id: brokerSnap.id,
              id_scgp: brokerSnap.id_scgp,
              previous_consultant_user_id: prevConsultantId,
              previous_consultant_name: prevConsultantName,
              consultant_user_id: newConsultantId,
              consultant_name: newLabel,
            },
          }
        )
      }

      closeConsultantRedistributeModal()
      await loadBrokers()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingConsultantRedistribute(false)
    }
  }

  const parseCsv = (value: string) =>
    value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)

  const openNewRuleModal = () => {
    const firstComercial = comercialUsers[0]?.id || ''
    setEditingRuleId(null)
    setNewRuleDraft({
      name: '',
      categories: [],
      states: [],
      is_fallback: false,
      priority: (rules.length || 0) + 1,
      comercial_user_id: firstComercial,
      is_active: true,
    })
    setIsNewRuleModalOpen(true)
  }

  const openEditRuleModal = (pageIndex: number) => {
    if (!canManage) return
    const absoluteIndex = (currentPageRules - 1) * itemsPerPage + pageIndex
    const rule = rules[absoluteIndex]
    if (!rule?.id) return
    setEditingRuleId(rule.id)
    setNewRuleDraft({
      id: rule.id,
      name: rule.name || '',
      categories: [...(rule.categories || [])],
      states: [...(rule.states || [])],
      is_fallback: !!rule.is_fallback,
      priority: rule.priority,
      comercial_user_id: rule.comercial_user_id,
      is_active: !!rule.is_active,
    })
    setIsNewRuleModalOpen(true)
  }

  const closeNewRuleModal = () => {
    setIsNewRuleModalOpen(false)
    setEditingRuleId(null)
  }

  const saveRuleFromModal = async () => {
    if (!newRuleDraft.comercial_user_id || newRuleDraft.categories.length === 0 || !canManage) return
    setSubmittingNewRule(true)
    setPageError(null)
    try {
      if (editingRuleId) {
        const prevRule = rules.find((r) => r.id === editingRuleId)
        await comercialGestaoService.updateRule({ ...newRuleDraft, id: editingRuleId })
        await logComercialGestao(
          'update',
          `Comercial gestão: editou regra de distribuição "${newRuleDraft.name || editingRuleId}".`,
          {
            entityId: editingRuleId,
            metadata: {
              rule_id: editingRuleId,
              old_values: prevRule ? ruleSnapshotForLog(prevRule) : undefined,
              new_values: ruleSnapshotForLog({ ...newRuleDraft, id: editingRuleId }),
            },
          }
        )
      } else {
        await comercialGestaoService.createRule(newRuleDraft)
        await logComercialGestao(
          'create',
          `Comercial gestão: cadastrou nova regra de distribuição "${newRuleDraft.name || '(sem nome)'}".`,
          { metadata: { new_rule: ruleSnapshotForLog(newRuleDraft) } }
        )
      }
      await loadStaticData()
      closeNewRuleModal()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setSubmittingNewRule(false)
    }
  }

  const removeRule = async (index: number) => {
    const absoluteIndex = (currentPageRules - 1) * itemsPerPage + index
    const rule = rules[absoluteIndex]
    if (!rule?.id) {
      setRules((prev) => prev.filter((_, i) => i !== absoluteIndex))
      return
    }

    setDeletingRule(true)
    setPageError(null)
    try {
      await comercialGestaoService.deleteRule(rule.id)
      await logComercialGestao('delete', `Comercial gestão: excluiu regra de distribuição "${rule.name || rule.id}".`, {
        entityId: rule.id,
        metadata: { deleted_rule: ruleSnapshotForLog(rule) },
      })
      await loadStaticData()
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setDeletingRule(false)
    }
  }

  const renderAssignmentContent = () => {
    if (isNewRuleModalOpen || !canInsert) return null
    if (activeTab !== 'gestao_comercial' && activeTab !== 'gestao_consultor') return null

    if (loadingGestaoTab || loadingBrokers) {
      return (
        <div className="flex justify-center py-20">
          <LoadingSpinner />
        </div>
      )
    }

    if (activeTab === 'gestao_comercial') {
      return (
        <>
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex flex-wrap gap-x-8" aria-label="Atribuição de comerciais">
              <button
                type="button"
                onClick={() => setGestaoParamTab('distribuicao_automatica')}
                className={gestaoParamTabClass(gestaoParamTab, 'distribuicao_automatica')}
              >
                Distribuição Automática
              </button>
              <button
                type="button"
                onClick={() => setGestaoParamTab('gestao_manual')}
                className={gestaoParamTabClass(gestaoParamTab, 'gestao_manual')}
              >
                Gestão Manual
              </button>
            </nav>
          </div>

          {gestaoParamTab === 'distribuicao_automatica' && (
            <>
              <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
                <nav className="-mb-px flex flex-wrap gap-x-4" aria-label="Distribuição automática">
                  <button
                    type="button"
                    onClick={() => setGestaoAutoSubTab('distribuicao')}
                    className={gestaoSubTabClass(gestaoAutoSubTab, 'distribuicao')}
                  >
                    Regras
                  </button>
                  <button
                    type="button"
                    onClick={() => setGestaoAutoSubTab('definido_automaticamente')}
                    className={gestaoSubTabClass(gestaoAutoSubTab, 'definido_automaticamente')}
                  >
                    Comercial definido automaticamente
                  </button>
                </nav>
              </div>

              {gestaoAutoSubTab === 'distribuicao' && (
                <>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mt-4">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                      <div>
                        <Heading as="h3" size="2xl">Regras dinâmicas de distribuição</Heading>
                        <Text size="sm" color="muted" className="mt-1">
                          Configure categorias, UFs e comercial responsável. Regras com menor prioridade são aplicadas primeiro.
                        </Text>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={openNewRuleModal} disabled={!canManage}>
                          Nova regra
                        </Button>
                      </div>
                    </div>

                    <div className="mb-3 flex items-center justify-between">
                      <Text size="sm" color="muted">
                        {rules.length} {rules.length === 1 ? 'regra cadastrada' : 'regras cadastradas'}
                      </Text>
                      <Text size="xs" color="muted">
                        Categorias e UFs por selecao multipla
                      </Text>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comercial</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Categorias</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">UFs</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ação</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {rules.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-6 text-center">
                                <Text size="sm" color="muted">Nenhuma regra cadastrada. Clique em "Nova regra".</Text>
                              </td>
                            </tr>
                          ) : (
                            pagedRules.map((rule, index) => (
                              <tr key={rule.id || `new-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-3 py-2">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-200">
                                    {rule.name || '—'}
                                  </Text>
                                </td>
                                <td className="px-3 py-2">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-200">
                                    {comercialUsers.find((u) => u.id === rule.comercial_user_id)?.name || '—'}
                                  </Text>
                                </td>
                                <td className="px-3 py-2">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-200">
                                    {rule.categories.length > 0 ? rule.categories.join(', ') : '—'}
                                  </Text>
                                </td>
                                <td className="px-3 py-2">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-200 whitespace-pre-line">
                                    {rule.states.length > 0 ? breakTextEvery(rule.states.join(', '), 20) : '—'}
                                  </Text>
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <div title="Editar regra">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openEditRuleModal(index)}
                                        disabled={!canManage || !rule.id}
                                        className="flex items-center justify-center"
                                        aria-label="Editar regra"
                                      >
                                        <PencilIcon className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div title="Remover regra">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setRuleIndexToRemove(index)}
                                        disabled={!canManage}
                                        className="flex items-center justify-center text-red-600 hover:text-red-700"
                                        aria-label="Remover regra"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      {totalPagesRules > 1 && (
                        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                          <Text size="sm" color="muted">
                            Página {currentPageRules} de {totalPagesRules}
                          </Text>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setCurrentPageRules((p) => Math.max(1, p - 1))} disabled={currentPageRules === 1}>
                              Anterior
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setCurrentPageRules((p) => Math.min(totalPagesRules, p + 1))} disabled={currentPageRules === totalPagesRules}>
                              Próximo
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <Text size="sm" color="muted">Elegíveis para regra automática</Text>
                      <Heading as="h3" size="2xl">{preview.totalEligible}</Heading>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <Text size="sm" color="muted">Vínculos auto a limpar</Text>
                      <Heading as="h3" size="2xl">{preview.totalToClear}</Heading>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <Text size="sm" color="muted">Ignorados na distribuição</Text>
                      <Heading as="h3" size="2xl">{preview.totalIgnoredManual}</Heading>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <Heading as="h3" size="2xl">Distribuição automática</Heading>
                        <Text size="sm" color="muted">
                          A distribuição automática usa as regras configuradas nesta tela. Corretores com vínculo
                          manual não terão o responsável alterado.
                        </Text>
                      </div>
                      <Button onClick={handleApplyAuto} disabled={!canManage || submittingAuto}>
                        {submittingAuto ? 'Aplicando...' : 'Aplicar distribuição automática'}
                      </Button>
                    </div>
                    {!canManage && (
                      <Text size="sm" color="muted" className="mt-2">
                        Você não possui permissão de alteração (insert/update) para este módulo.
                      </Text>
                    )}
                    <div className="mt-3">
                      {preview.byComercial.map((line) => (
                        <Text key={line.comercialName} size="sm" color="muted">
                          {line.comercialName}: {line.count}
                        </Text>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {gestaoAutoSubTab === 'definido_automaticamente' && (
                <>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mt-4 max-w-sm">
                    <Text size="sm" color="muted">Automático com responsável</Text>
                    <Heading as="h3" size="2xl">{autoAssignedItems.length}</Heading>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto mt-4">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-3 items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <Heading as="h3" size="2xl">Comercial definido automaticamente</Heading>
                        <Text size="sm" color="muted" className="mt-1">
                          Corretores com vínculo automático e responsável já atribuído pelas regras de distribuição.
                          Use Redistribuir para trocar ou remover o comercial.
                        </Text>
                        {!canManage && (
                          <Text size="sm" color="muted" className="mt-2">
                            Você não possui permissão de alteração (insert/update) para redistribuir responsáveis.
                          </Text>
                        )}
                      </div>
                      <Input
                        placeholder="Pesquisar por nome do corretor..."
                        value={filterAutoAssignedName}
                        onChange={(e) => setFilterAutoAssignedName(e.target.value)}
                        className="min-w-[200px] max-w-full w-64 shrink-0"
                      />
                    </div>
                    <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corretor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código SCGP</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UF/Cidade</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsável atual</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ação</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {autoAssignedItems.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center">
                              <Text size="sm" color="muted">Nenhum corretor com atribuição automática e responsável definido.</Text>
                            </td>
                          </tr>
                        ) : autoAssignedFiltered.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center">
                              <Text size="sm" color="muted">Nenhum corretor encontrado com esse nome.</Text>
                            </td>
                          </tr>
                        ) : (
                          pagedAutoAssigned.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.id_scgp ?? '—'}</td>
                              <ClassificationCell
                                classification={item.classification}
                                classificationPrevious={item.classification_previous}
                                rankMap={classificationRankMap}
                                colorMap={classificationColorMap}
                                hasProduction={item.has_production}
                                monthlyPaidPremium={item.monthly_paid_premium}
                                classificationBands={classificationBands}
                              />
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                {[item.state, item.city].filter(Boolean).join(' / ') || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.comercial_name || '—'}</td>
                              <td className="px-4 py-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openRedistributeModal(item)}
                                  disabled={!canManage}
                                >
                                  Redistribuir
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {totalPagesAutoAssigned > 1 && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <Text size="sm" color="muted">
                          Página {currentPageAutoAssigned} de {totalPagesAutoAssigned}
                        </Text>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setCurrentPageAutoAssigned((p) => Math.max(1, p - 1))} disabled={currentPageAutoAssigned === 1}>
                            Anterior
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCurrentPageAutoAssigned((p) => Math.min(totalPagesAutoAssigned, p + 1))} disabled={currentPageAutoAssigned === totalPagesAutoAssigned}>
                            Próximo
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {gestaoParamTab === 'gestao_manual' && (
            <>
              <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
                <nav className="-mb-px flex flex-wrap gap-x-4" aria-label="Gestão manual">
                  <button
                    type="button"
                    onClick={() => setGestaoManualSubTab('gestao_manual')}
                    className={gestaoSubTabClass(gestaoManualSubTab, 'gestao_manual')}
                  >
                    Pendentes de atribuição
                  </button>
                  <button
                    type="button"
                    onClick={() => setGestaoManualSubTab('definido_manualmente')}
                    className={gestaoSubTabClass(gestaoManualSubTab, 'definido_manualmente')}
                  >
                    Comercial definido manualmente
                  </button>
                </nav>
              </div>

              {gestaoManualSubTab === 'gestao_manual' && (
                <>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mt-4 max-w-sm">
                    <Text size="sm" color="muted">Pendente Gestão Manual</Text>
                    <Heading as="h3" size="2xl">{manualPendingItems.length}</Heading>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto mt-4">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center justify-between">
                      <Heading as="h3" size="2xl">Gestão Manual</Heading>
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          placeholder="Pesquisar por nome do corretor..."
                          value={filterManualPendingName}
                          onChange={(e) => setFilterManualPendingName(e.target.value)}
                          className="min-w-[200px] max-w-full w-64"
                        />
                        <Select value={manualComercialId} onChange={(e) => setManualComercialId(e.target.value)}>
                          <option value="">Selecionar comercial...</option>
                          {comercialUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </Select>
                        <Button
                          onClick={handleAssignManual}
                          disabled={!canManage || !manualComercialId || selectedBrokers.size === 0 || submittingManual}
                        >
                          {submittingManual ? 'Salvando...' : `Atribuir selecionados (${selectedBrokers.size})`}
                        </Button>
                      </div>
                    </div>
                    <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            <input
                              type="checkbox"
                              checked={
                                pagedPendentes.length > 0 &&
                                pagedPendentes.every((i) => selectedBrokers.has(i.id))
                              }
                              onChange={toggleSelectAllManualPending}
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corretor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código SCGP</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UF/Cidade</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comercial atual</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {manualPendingItems.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center">
                              <Text size="sm" color="muted">Nenhum corretor aguardando responsável manual.</Text>
                            </td>
                          </tr>
                        ) : manualPendingFiltered.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center">
                              <Text size="sm" color="muted">Nenhum corretor encontrado com esse nome.</Text>
                            </td>
                          </tr>
                        ) : (
                          pagedPendentes.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedBrokers.has(item.id)}
                                  onChange={() => toggleBroker(item.id)}
                                />
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.id_scgp ?? '—'}</td>
                              <ClassificationCell
                                classification={item.classification}
                                classificationPrevious={item.classification_previous}
                                rankMap={classificationRankMap}
                                colorMap={classificationColorMap}
                                hasProduction={item.has_production}
                                monthlyPaidPremium={item.monthly_paid_premium}
                                classificationBands={classificationBands}
                              />
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                {[item.state, item.city].filter(Boolean).join(' / ') || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.comercial_name || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {totalPagesPendentes > 1 && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <Text size="sm" color="muted">
                          Página {currentPagePendentes} de {totalPagesPendentes}
                        </Text>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setCurrentPagePendentes((p) => Math.max(1, p - 1))} disabled={currentPagePendentes === 1}>
                            Anterior
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCurrentPagePendentes((p) => Math.min(totalPagesPendentes, p + 1))} disabled={currentPagePendentes === totalPagesPendentes}>
                            Próximo
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {gestaoManualSubTab === 'definido_manualmente' && (
                <>
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mt-4 max-w-sm">
                    <Text size="sm" color="muted">Manual com responsável</Text>
                    <Heading as="h3" size="2xl">{manualAssignedItems.length}</Heading>
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto mt-4">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-3 items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <Heading as="h3" size="2xl">Comercial definido manualmente</Heading>
                        <Text size="sm" color="muted" className="mt-1">
                          Corretores com vínculo manual e responsável já atribuído. Use Redistribuir para trocar ou remover o comercial.
                        </Text>
                        {!canManage && (
                          <Text size="sm" color="muted" className="mt-2">
                            Você não possui permissão de alteração (insert/update) para redistribuir responsáveis.
                          </Text>
                        )}
                      </div>
                      <Input
                        placeholder="Pesquisar por nome do corretor..."
                        value={filterManualAssignedName}
                        onChange={(e) => setFilterManualAssignedName(e.target.value)}
                        className="min-w-[200px] max-w-full w-64 shrink-0"
                      />
                    </div>
                    <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corretor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código SCGP</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UF/Cidade</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsável atual</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ação</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {manualAssignedItems.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center">
                              <Text size="sm" color="muted">Nenhum corretor com atribuição manual e responsável definido.</Text>
                            </td>
                          </tr>
                        ) : manualAssignedFiltered.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="px-4 py-8 text-center">
                              <Text size="sm" color="muted">Nenhum corretor encontrado com esse nome.</Text>
                            </td>
                          </tr>
                        ) : (
                          pagedManualAssigned.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.name}</td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.id_scgp ?? '—'}</td>
                              <ClassificationCell
                                classification={item.classification}
                                classificationPrevious={item.classification_previous}
                                rankMap={classificationRankMap}
                                colorMap={classificationColorMap}
                                hasProduction={item.has_production}
                                monthlyPaidPremium={item.monthly_paid_premium}
                                classificationBands={classificationBands}
                              />
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                {[item.state, item.city].filter(Boolean).join(' / ') || '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.comercial_name || '—'}</td>
                              <td className="px-4 py-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openRedistributeModal(item)}
                                  disabled={!canManage}
                                >
                                  Redistribuir
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    {totalPagesManualAssigned > 1 && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <Text size="sm" color="muted">
                          Página {currentPageManualAssigned} de {totalPagesManualAssigned}
                        </Text>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setCurrentPageManualAssigned((p) => Math.max(1, p - 1))} disabled={currentPageManualAssigned === 1}>
                            Anterior
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCurrentPageManualAssigned((p) => Math.min(totalPagesManualAssigned, p + 1))} disabled={currentPageManualAssigned === totalPagesManualAssigned}>
                            Próximo
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )
    }

    return (
      <>
        <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
          <nav className="-mb-px flex flex-wrap gap-x-4" aria-label="Atribuição de consultor">
            <button
              type="button"
              onClick={() => setGestaoConsultorSubTab('gestao_manual')}
              className={gestaoSubTabClass(gestaoConsultorSubTab, 'gestao_manual')}
            >
              Pendentes de atribuição
            </button>
            <button
              type="button"
              onClick={() => setGestaoConsultorSubTab('definido_manualmente')}
              className={gestaoSubTabClass(gestaoConsultorSubTab, 'definido_manualmente')}
            >
              Consultor definido manualmente
            </button>
          </nav>
        </div>

        {gestaoConsultorSubTab === 'gestao_manual' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mt-4 max-w-sm">
              <Text size="sm" color="muted">Pendente Atribuição de Consultor</Text>
              <Heading as="h3" size="2xl">{consultantPendingItems.length}</Heading>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto mt-4">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center justify-between">
                <Heading as="h3" size="2xl">Atribuição de Consultor</Heading>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    placeholder="Pesquisar por nome do corretor..."
                    value={filterConsultantPendingName}
                    onChange={(e) => setFilterConsultantPendingName(e.target.value)}
                    className="min-w-[200px] max-w-full w-64"
                  />
                  <Select value={manualConsultantId} onChange={(e) => setManualConsultantId(e.target.value)}>
                    <option value="">Selecionar consultor...</option>
                    {consultantUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                  <Button
                    onClick={handleAssignConsultantManual}
                    disabled={!canManage || !manualConsultantId || selectedConsultantBrokers.size === 0 || submittingConsultantManual}
                  >
                    {submittingConsultantManual ? 'Salvando...' : `Atribuir selecionados (${selectedConsultantBrokers.size})`}
                  </Button>
                </div>
              </div>
              <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <input
                        type="checkbox"
                        checked={
                          pagedConsultantPendentes.length > 0 &&
                          pagedConsultantPendentes.every((i) => selectedConsultantBrokers.has(i.id))
                        }
                        onChange={toggleSelectAllConsultantPending}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corretor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código SCGP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UF/Cidade</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consultor atual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {consultantPendingItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <Text size="sm" color="muted">Nenhum corretor aguardando consultor manual.</Text>
                      </td>
                    </tr>
                  ) : consultantPendingFiltered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <Text size="sm" color="muted">Nenhum corretor encontrado com esse nome.</Text>
                      </td>
                    </tr>
                  ) : (
                    pagedConsultantPendentes.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedConsultantBrokers.has(item.id)}
                            onChange={() => toggleConsultantBroker(item.id)}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.id_scgp ?? '—'}</td>
                        <ClassificationCell
                          classification={item.classification}
                          classificationPrevious={item.classification_previous}
                          rankMap={classificationRankMap}
                          colorMap={classificationColorMap}
                          hasProduction={item.has_production}
                          monthlyPaidPremium={item.monthly_paid_premium}
                          classificationBands={classificationBands}
                        />
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {[item.state, item.city].filter(Boolean).join(' / ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.consultant_name || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {totalPagesConsultantPendentes > 1 && (
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Text size="sm" color="muted">
                    Página {currentPageConsultantPendentes} de {totalPagesConsultantPendentes}
                  </Text>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPageConsultantPendentes((p) => Math.max(1, p - 1))} disabled={currentPageConsultantPendentes === 1}>
                      Anterior
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPageConsultantPendentes((p) => Math.min(totalPagesConsultantPendentes, p + 1))} disabled={currentPageConsultantPendentes === totalPagesConsultantPendentes}>
                      Próximo
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {gestaoConsultorSubTab === 'definido_manualmente' && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mt-4 max-w-sm">
              <Text size="sm" color="muted">Manual com consultor</Text>
              <Heading as="h3" size="2xl">{consultantAssignedItems.length}</Heading>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto mt-4">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-3 items-start justify-between">
                <div className="min-w-0 flex-1">
                  <Heading as="h3" size="2xl">Consultor definido manualmente</Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    Corretores com vínculo manual de consultor já atribuído. Use Redistribuir para trocar ou remover o consultor.
                  </Text>
                  {!canManage && (
                    <Text size="sm" color="muted" className="mt-2">
                      Você não possui permissão de alteração (insert/update) para redistribuir consultores.
                    </Text>
                  )}
                </div>
                <Input
                  placeholder="Pesquisar por nome do corretor..."
                  value={filterConsultantAssignedName}
                  onChange={(e) => setFilterConsultantAssignedName(e.target.value)}
                  className="min-w-[200px] max-w-full w-64 shrink-0"
                />
              </div>
              <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corretor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código SCGP</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UF/Cidade</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consultor atual</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {consultantAssignedItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <Text size="sm" color="muted">Nenhum corretor com atribuição manual de consultor.</Text>
                      </td>
                    </tr>
                  ) : consultantAssignedFiltered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <Text size="sm" color="muted">Nenhum corretor encontrado com esse nome.</Text>
                      </td>
                    </tr>
                  ) : (
                    pagedConsultantAssigned.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.id_scgp ?? '—'}</td>
                        <ClassificationCell
                          classification={item.classification}
                          classificationPrevious={item.classification_previous}
                          rankMap={classificationRankMap}
                          colorMap={classificationColorMap}
                          hasProduction={item.has_production}
                          monthlyPaidPremium={item.monthly_paid_premium}
                          classificationBands={classificationBands}
                        />
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {[item.state, item.city].filter(Boolean).join(' / ') || '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.consultant_name || '—'}</td>
                        <td className="px-4 py-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openConsultantRedistributeModal(item)}
                            disabled={!canManage}
                          >
                            Redistribuir
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {totalPagesConsultantAssigned > 1 && (
                <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <Text size="sm" color="muted">
                    Página {currentPageConsultantAssigned} de {totalPagesConsultantAssigned}
                  </Text>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setCurrentPageConsultantAssigned((p) => Math.max(1, p - 1))} disabled={currentPageConsultantAssigned === 1}>
                      Anterior
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setCurrentPageConsultantAssigned((p) => Math.min(totalPagesConsultantAssigned, p + 1))} disabled={currentPageConsultantAssigned === totalPagesConsultantAssigned}>
                      Próximo
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </>
    )
  }

  return (
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName="comercial_gestao"
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">Acesso Negado</Text>
                <Text size="sm" color="muted">Você não tem permissão para acessar esta página.</Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleInfo?.display_name || 'Gestão Comercial'}
              </Heading>
              {moduleInfo?.description && (
                <Text size="sm" color="muted" className="mt-1">
                  {moduleInfo.description}
                </Text>
              )}

            </div>

            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('listagem')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'listagem'
                        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    Relação Completa
                  </button>
                  {!loadingCadastroPermission && canInsert && (
                    <button
                      type="button"
                      onClick={() => goToGestaoTab('gestao_comercial')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'gestao_comercial'
                          ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      Atribuição de Comercial
                    </button>
                  )}
                  {!loadingCadastroPermission && canInsert && (
                    <button
                      type="button"
                      onClick={() => goToGestaoTab('gestao_consultor')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'gestao_consultor'
                          ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      Atribuição de Consultor
                    </button>
                  )}
                </nav>
              </div>
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {pageError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    <Text size="sm">{pageError}</Text>
                  </div>
                )}
                {!isNewRuleModalOpen && activeTab === 'listagem' && (
                  <>
                    <ClassificationKpiCards
                      kpis={classificationKpis}
                      loading={loadingClassificationKpis}
                      error={classificationKpisError}
                    />

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <div className="mb-4">
                        <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">Filtros</Heading>
                      </div>
                      <div
                        className={`grid grid-cols-1 md:grid-cols-2 gap-3 ${
                          isStartClassificationSelected ? 'lg:grid-cols-9' : 'lg:grid-cols-8'
                        }`}
                      >
                        <div className="min-w-0 lg:col-span-2">
                          <Input
                            placeholder="Nome do corretor"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div className="min-w-0">
                          <Input
                            placeholder="Código SCGP"
                            value={filterCode}
                            onChange={(e) => setFilterCode(e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div className="min-w-0">
                        <Select
                          value={filterClassification}
                          onChange={(e) => {
                            const next = e.target.value
                            setFilterClassification(next)
                            if (normalizeClassificationKey(next) !== 'START') {
                              setFilterStartProduction('')
                            }
                          }}
                          className="w-full"
                        >
                          <option value="">Todas categorias</option>
                          {classificationFilterOptions.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </Select>
                        </div>
                        <div className="min-w-0">
                        <Select
                          value={filterClassificationTrend}
                          onChange={(e) => setFilterClassificationTrend(e.target.value as ClassificationTrendFilter)}
                          className="w-full"
                        >
                          <option value="">Variação de categoria</option>
                          <option value="upgrade">Subiu</option>
                          <option value="downgrade">Desceu</option>
                          <option value="same">Manteve</option>
                          <option value="none">Sem indicador</option>
                        </Select>
                        </div>
                        {isStartClassificationSelected && (
                          <div className="min-w-0">
                          <Select
                            value={filterStartProduction}
                            onChange={(e) => setFilterStartProduction(e.target.value as StartProductionFilter)}
                            className="w-full"
                          >
                            <option value="">Com e Sem Produção</option>
                            <option value="with">Com produção</option>
                            <option value="without">Sem produção</option>
                          </Select>
                          </div>
                        )}
                        <div className="min-w-0">
                        <Select value={filterUf} onChange={(e) => setFilterUf(e.target.value)} className="w-full">
                          <option value="">Todas UFs</option>
                          {ufs.map((uf) => (
                            <option key={uf} value={uf}>
                              {uf}
                            </option>
                          ))}
                        </Select>
                        </div>
                        <div className="min-w-0">
                        <Select
                          value={displayedComercialFilter}
                          onChange={(e) => setFilterComercial(e.target.value)}
                          disabled={isComercialFilterRestricted}
                          className="w-full"
                        >
                          {!isComercialFilterRestricted && <option value="">Comercial</option>}
                          {comercialUsers.map((u) => (
                            <option key={u.id} value={u.name}>
                              {u.name}
                            </option>
                          ))}
                        </Select>
                        </div>
                        <div className="min-w-0">
                        <Select
                          value={filterConsultant}
                          onChange={(e) => setFilterConsultant(e.target.value)}
                          className="w-full"
                        >
                          <option value="">Consultor</option>
                          {consultantUsers.map((u) => (
                            <option key={u.id} value={u.name}>
                              {u.name}
                            </option>
                          ))}
                        </Select>
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button
                          variant="solid"
                          size="sm"
                          onClick={applyListagemFilters}
                          className="mr-2"
                          disabled={loadingBrokers}
                        >
                          Buscar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setFilterName('')
                            setFilterCode('')
                            setFilterClassification('')
                            setFilterClassificationTrend('')
                            setFilterStartProduction('')
                            setFilterUf('')
                            if (!isComercialFilterRestricted) {
                              setFilterComercial('')
                            }
                            setFilterConsultant('')
                            setAppliedFilters({
                              name: '',
                              code: '',
                              classification: '',
                              classificationTrend: '',
                              startProduction: '',
                              uf: '',
                              comercial: isComercialFilterRestricted ? loggedUserName : '',
                              consultant: '',
                            })
                            setCurrentPageListagem(1)
                            setHasSearchedListagem(false)
                            setItems([])
                          }}
                          disabled={loadingBrokers || (!hasSearchedListagem && !hasListagemFiltersActive)}
                        >
                          Limpar filtros
                        </Button>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                        <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                          Corretores
                          <span className="ml-2 text-base font-normal text-gray-500 dark:text-gray-400">
                            ({filteredItems.length} registros)
                          </span>
                        </Heading>
                        {canDownloadInfo && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void exportListagemExcel()}
                          disabled={!hasSearchedListagem || loadingBrokers || filteredItems.length === 0 || exportingExcel}
                          title={
                            !hasSearchedListagem
                              ? 'Busque os corretores antes de exportar'
                              : filteredItems.length === 0
                                ? 'Nenhum registro para exportar'
                                : 'Baixar Excel com a relação filtrada e aba de contatos'
                          }
                        >
                          {exportingExcel ? (
                            <span className="inline-flex items-center gap-1.5">
                              <LoadingSpinner size="sm" />
                              Exportando...
                            </span>
                          ) : (
                            <>
                              <ArrowDownTrayIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
                              Baixar Excel
                            </>
                          )}
                        </Button>
                        )}
                      </div>
                      <table className="w-full min-w-[900px] divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corretor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código SCGP</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UF/Cidade</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comercial</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consultor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {!hasSearchedListagem ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">
                                  Clique em Buscar para carregar os registros.
                                </Text>
                              </td>
                            </tr>
                          ) : loadingBrokers ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center">
                                <div className="flex items-center justify-center">
                                  <LoadingSpinner />
                                </div>
                              </td>
                            </tr>
                          ) : filteredItems.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 py-8 text-center">
                                <Text size="sm" color="muted">Nenhum corretor encontrado com os filtros aplicados.</Text>
                              </td>
                            </tr>
                          ) : (
                            pagedListagem.map((item) => (
                              <tr
                                key={item.id}
                                role="button"
                                tabIndex={0}
                                title="Ver contatos do corretor"
                                onClick={() => openContactsModal(item)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    openContactsModal(item)
                                  }
                                }}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                              >
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 underline-offset-2 hover:underline max-w-[40ch] whitespace-normal break-words">
                                  {item.name}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.id_scgp ?? '—'}</td>
                                <ClassificationCell
                                  classification={item.classification}
                                  classificationPrevious={item.classification_previous}
                                  rankMap={classificationRankMap}
                                  colorMap={classificationColorMap}
                                  hasProduction={item.has_production}
                                  monthlyPaidPremium={item.monthly_paid_premium}
                                  classificationBands={classificationBands}
                                />
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                                  {[item.state, item.city].filter(Boolean).join(' / ') || '—'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.comercial_name || '—'}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.consultant_name || '—'}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      {totalPagesListagem > 1 && (
                        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                          <Text size="sm" color="muted">
                            Página {currentPageListagem} de {totalPagesListagem}
                          </Text>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setCurrentPageListagem((p) => Math.max(1, p - 1))} disabled={currentPageListagem === 1}>
                              Anterior
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setCurrentPageListagem((p) => Math.min(totalPagesListagem, p + 1))} disabled={currentPageListagem === totalPagesListagem}>
                              Próximo
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {renderAssignmentContent()}
              </div>
            )}
          </div>
        </PermissionGuard>

        {isNewRuleModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                  {editingRuleId ? 'Editar regra de distribuição' : 'Nova regra de distribuição'}
                </Heading>
                <Button variant="ghost" onClick={closeNewRuleModal}>
                  Fechar
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Nome da regra</Text>
                  <Input
                    value={newRuleDraft.name}
                    placeholder="Ex: Capitais - categorias altas"
                    onChange={(e) => setNewRuleDraft((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Comercial</Text>
                  <Select
                    value={newRuleDraft.comercial_user_id}
                    onChange={(e) => setNewRuleDraft((prev) => ({ ...prev, comercial_user_id: e.target.value }))}
                  >
                    <option value="">Selecionar...</option>
                    {comercialUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="md:col-span-2">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Categorias</Text>
                  <CheckboxGrid
                    options={availableCategories}
                    selected={newRuleDraft.categories}
                    onToggle={(value, checked) => {
                      setNewRuleDraft((prev) => {
                        const next = new Set(prev.categories.map((c) => c.toUpperCase()))
                        if (checked) next.add(value.toUpperCase())
                        else next.delete(value.toUpperCase())
                        return { ...prev, categories: Array.from(next) }
                      })
                    }}
                  />
                </div>

                <div className="md:col-span-2">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">UFs</Text>
                  <CheckboxGrid
                    options={BR_UFS}
                    selected={newRuleDraft.states}
                    getDisplayLabel={(uf) => BR_STATE_NAMES[uf.toUpperCase()] ?? uf}
                    onToggle={(value, checked) => {
                      setNewRuleDraft((prev) => {
                        const next = new Set(prev.states.map((s) => s.toUpperCase()))
                        if (checked) next.add(value.toUpperCase())
                        else next.delete(value.toUpperCase())
                        return { ...prev, states: Array.from(next) }
                      })
                    }}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <Button variant="ghost" onClick={closeNewRuleModal}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => void saveRuleFromModal()}
                  disabled={!newRuleDraft.comercial_user_id || newRuleDraft.categories.length === 0 || submittingNewRule}
                >
                  {submittingNewRule
                    ? editingRuleId
                      ? 'Salvando...'
                      : 'Adicionando...'
                    : editingRuleId
                      ? 'Salvar alterações'
                      : 'Adicionar regra'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {ruleIndexToRemove !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <Heading as="h3" size="2xl" className="text-red-600 dark:text-red-400">
                  Excluir regra
                </Heading>
              </div>

              <div className="mb-6">
                <div className="mb-4">
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    Tem certeza que deseja excluir esta regra?
                  </Text>
                  <Text size="sm" color="muted">
                    Esta ação não pode ser desfeita.
                  </Text>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setRuleIndexToRemove(null)}>
                  Cancelar
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={deletingRule}
                  onClick={async () => {
                    await removeRule(ruleIndexToRemove)
                    setRuleIndexToRemove(null)
                  }}
                >
                  {deletingRule ? 'Removendo...' : 'Remover'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {contactsModalBroker && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={closeContactsModal}
            role="presentation"
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto mx-4"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-labelledby="contacts-modal-title"
              aria-modal="true"
            >
              <div className="mb-4">
                <Heading as="h3" size="2xl" id="contacts-modal-title" className="text-gray-900 dark:text-white">
                  Contatos do corretor
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  Corretor:{' '}
                  <span className="text-gray-800 dark:text-gray-200 font-medium">{contactsModalBroker.name}</span>
                  {contactsModalBroker.id_scgp != null && (
                    <>
                      {' '}
                      · SCGP:{' '}
                      <span className="text-gray-800 dark:text-gray-200 font-medium">
                        {contactsModalBroker.id_scgp}
                      </span>
                    </>
                  )}
                </Text>
              </div>

              <div className="mb-6">
                {contactsModalLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner />
                  </div>
                ) : contactsModalError ? (
                  <Text size="sm" className="text-red-600 dark:text-red-400">
                    {contactsModalError}
                  </Text>
                ) : contactsModalItems.length === 0 ? (
                  <Text size="sm" color="muted">
                    Nenhum contato cadastrado para este corretor.
                  </Text>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="w-full min-w-[640px] divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sócio</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departamento</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cargo</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefone</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-mail</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {contactsModalItems.map((contact) => (
                          <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                              {contact.name || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {contact.is_partner ? (
                                <span className="inline-flex items-center rounded-full bg-primary-100 dark:bg-primary-900/40 px-2 py-0.5 text-xs font-medium text-primary-800 dark:text-primary-200">
                                  Sim
                                </span>
                              ) : (
                                'Não'
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {contact.department || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {contact.role || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {contact.phone ? (
                                <a
                                  href={`tel:${contact.phone.replace(/\s/g, '')}`}
                                  className="text-primary-600 dark:text-primary-400 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {contact.phone}
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 align-top">
                              {contact.email ? <ContactEmailCell email={contact.email} /> : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={closeContactsModal}>
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        )}

        {redistributeModalBroker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <div className="mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Redistribuir comercial
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  Corretor: <span className="text-gray-800 dark:text-gray-200 font-medium">{redistributeModalBroker.name}</span>
                </Text>
              </div>

              <div className="mb-6">
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Responsável comercial</Text>
                <Select value={redistributeComercialId} onChange={(e) => setRedistributeComercialId(e.target.value)}>
                  <option value="">Selecionar...</option>
                  <option value={REDISTRIBUTE_CLEAR_COMMERCIAL}>Sem comercial (remover vínculo)</option>
                  {comercialUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
                <Text size="xs" color="muted" className="mt-2 block">
                  Ao remover o vínculo, o corretor deixa de ter responsável manual até uma nova atribuição.
                </Text>
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={closeRedistributeModal} disabled={submittingRedistribute}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => void handleConfirmRedistribute()}
                  disabled={!canManage || !redistributeComercialId || submittingRedistribute}
                  className={
                    redistributeComercialId === REDISTRIBUTE_CLEAR_COMMERCIAL
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : undefined
                  }
                >
                  {submittingRedistribute
                    ? 'Salvando...'
                    : redistributeComercialId === REDISTRIBUTE_CLEAR_COMMERCIAL
                      ? 'Remover vínculo'
                      : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {redistributeConsultantModalBroker && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <div className="mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Redistribuir consultor
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  Corretor: <span className="text-gray-800 dark:text-gray-200 font-medium">{redistributeConsultantModalBroker.name}</span>
                </Text>
              </div>

              <div className="mb-6">
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Consultor responsável</Text>
                <Select value={redistributeConsultantId} onChange={(e) => setRedistributeConsultantId(e.target.value)}>
                  <option value="">Selecionar...</option>
                  <option value={REDISTRIBUTE_CLEAR_CONSULTANT}>Sem consultor (remover vínculo)</option>
                  {consultantUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
                <Text size="xs" color="muted" className="mt-2 block">
                  Ao remover o vínculo, o corretor deixa de ter consultor manual até uma nova atribuição.
                </Text>
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={closeConsultantRedistributeModal} disabled={submittingConsultantRedistribute}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => void handleConfirmConsultantRedistribute()}
                  disabled={!canManage || !redistributeConsultantId || submittingConsultantRedistribute}
                  className={
                    redistributeConsultantId === REDISTRIBUTE_CLEAR_CONSULTANT
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : undefined
                  }
                >
                  {submittingConsultantRedistribute
                    ? 'Salvando...'
                    : redistributeConsultantId === REDISTRIBUTE_CLEAR_CONSULTANT
                      ? 'Remover vínculo'
                      : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
  )
}

export default GestaoComercial
